import {
  Box,
  Flex,
  rem,
  rgba,
  Text,
  Tooltip,
  useMantineColorScheme,
} from "@mantine/core";
import {
  IconCircleCheckFilled,
  IconInfoCircleFilled,
} from "@tabler/icons-react";
import React, { useMemo } from "react";
import { checkPasswordStrength } from "@/shared/utils/password";

type PasswordRule = {
  id: number;
  rule: RegExp;
  label: string;
};

type ControlledPasswordRule = PasswordRule & {
  satisfied: boolean;
};

type StrengthLevel = {
  label: string;
  color: string;
};

interface PasswordStrengthProps {
  labelPosition?: "top" | "bottom" | "left" | "right";
  password: string;
  levels: readonly StrengthLevel[];
  tooltipLabel?: string;
  showTooltip?: boolean;
  showStrength?: boolean;
  /**
   * Reserva a altura da linha de força mesmo antes dela aparecer, evitando que
   * o layout "pule" quando o usuário começa a digitar. Útil em formulários onde
   * o crescimento empurraria elementos abaixo (ex.: o botão de submit no signup).
   */
  reserveStrengthRow?: boolean;
}

const tooltipDefaultLabel = `Sua senha precisa ter pelo menos 8 caracteres, incluindo letras maiúsculas, minúsculas, números e símbolos especiais`;

const labelPositions = {
  top: "column" as const,
  bottom: "column-reverse" as const,
  left: "row" as const,
  right: "row-reverse" as const,
} as const;

export function PasswordStrength({
  levels,
  labelPosition = "left",
  password,
  tooltipLabel = tooltipDefaultLabel,
  showTooltip = true,
  showStrength = true,
  reserveStrengthRow = false,
}: PasswordStrengthProps) {
  const { colorScheme } = useMantineColorScheme();

  const { controlledRules, percentage } = useMemo(
    () => checkPasswordStrength(password),
    [password]
  );

  const currentLevelIndex = useMemo(() => {
    if (percentage < 0.5) return 0; // Fraca
    if (percentage < 1) return 1; // Média
    return 2; // Forte
  }, [percentage]);

  const currentLevel = levels[currentLevelIndex];
  const labelInYAxis = ["top", "bottom"].includes(labelPosition);

  return (
    <Flex
      w={"100%"}
      h={"100%"}
      direction={"column"}
      gap={12}>
      <Flex
        w={"100%"}
        h={"100%"}
        // Reserva a altura da linha de força (rótulo + barras ~24px) para o
        // layout não crescer quando ela aparece ao digitar a senha.
        mih={reserveStrengthRow ? rem(24) : undefined}
        direction={labelPositions[labelPosition]}
        align={labelInYAxis ? "normal" : "center"}
        gap={8}>
        {showStrength && currentLevel && (
          <Text
            c={currentLevel.color}
            fw={700}
            fz={16}
            style={{ whiteSpace: "nowrap" }}>
            {currentLevel.label}
          </Text>
        )}

        {showStrength && (
          <Flex
            w="100%"
            h={11}
            gap={4}>
            {levels.map((level, index) => {
              const isActive = index <= currentLevelIndex;
              const barColor = isActive
                ? currentLevel?.color || level.color
                : "var(--mantine-color-L500-0)";

              return (
                <Box
                  key={level.label}
                  style={{
                    flex: 1,
                    height: "100%",
                    backgroundColor: barColor,
                    transition: "background-color 250ms ease",
                    borderRadius:
                      index === 0
                        ? "8px 0 0 8px"
                        : index === levels.length - 1
                          ? "0 8px 8px 0"
                          : "0",
                  }}
                />
              );
            })}
          </Flex>
        )}
      </Flex>

      {showTooltip && (
        <Flex
          direction="row"
          align="flex-start"
          gap={8}>
          <Tooltip
            multiline
            w={300}
            withArrow
            label={
              <Flex
                direction="column"
                gap={8}
                p={4}>
                <TooltipLabel rules={controlledRules} />
              </Flex>
            }>
            <Box
              style={{ cursor: "help", display: "flex", paddingTop: rem(2) }}>
              <IconInfoCircleFilled
                size={20}
                color={rgba(
                  colorScheme === "dark"
                    ? "var(--mantine-color-L300-0)"
                    : "var(--mantine-color-D900-0)",
                  0.5
                )}
              />
            </Box>
          </Tooltip>
          <Text
            ff={"heading"}
            fz={14}
            lh={rem(22)}
            c={rgba(
              colorScheme === "dark"
                ? "var(--mantine-color-L300-0)"
                : "var(--mantine-color-D900-0)",
              0.5
            )}>
            {tooltipLabel}
          </Text>
        </Flex>
      )}
    </Flex>
  );
}

interface TooltipLabelProps {
  rules: ControlledPasswordRule[];
}

function TooltipLabel({ rules }: TooltipLabelProps) {
  return (
    <Flex
      direction={"column"}
      gap={4}>
      {rules.map((rule) => {
        return (
          <React.Fragment key={rule.id}>
            <Flex
              direction={"row"}
              gap={8}
              align={"center"}>
              {rule.satisfied ? (
                <IconCircleCheckFilled
                  size={16}
                  color="var(--mantine-color-teal-6)"
                />
              ) : (
                <IconInfoCircleFilled
                  size={16}
                  color="var(--mantine-color-gray-6)"
                />
              )}
              <Text
                fz={11}
                c={
                  rule.satisfied
                    ? "var(--mantine-color-teal-6)"
                    : "var(--mantine-color-gray-6)"
                }>
                {rule.label}
              </Text>
            </Flex>
          </React.Fragment>
        );
      })}
    </Flex>
  );
}
